/**
 * Inspector feature module (W2-06; WORKPACKAGES Appendix B: `inspector.*`, `project.settings`).
 *
 * Provides:
 *  - panel `inspector` (slot `chart.inspector`) and the inspector sections: note 100, several notes 101, text
 *    segment 190, segment 200, keyframe 210, line 300, N LINES 301, tempo point 400, marker 450;
 *  - dialogs `DIALOG.textMode` and `DIALOG.projectSettings`;
 *  - commands `project.settings` (Project menu, inspector header, palette), `inspector.focusField` (lane
 *    double-click, Problems "Go", checker fixes; chart-level `pkg.*` / `meta.preview*` fields open Project settings
 *    on that field, music / illustration fields show the Assets panel, the other package metadata (`meta.name`,
 *    `meta.tags`...) opens the Publish workspace), `inspector.focus`, `inspector.toggleUnits`,
 *    `inspector.setAtPlayhead` (N LINES "Set at playhead" from the palette), `inspector.clampPreviewRange`,
 *    `inspector.textMode`;
 *  - help topics (expressions, project settings, units);
 *  - `init`: after the project music is replaced, a preview range that no longer fits the song is reported with a
 *    Clamp action (Phira refuses to open such a chart).
 */

import { DIALOG } from '../../app/dialogs/types';
import type { CommandArgs } from '../../app/registry/commands';
import type { AppServices, CommandContext, CommandDef, FeatureModule, HelpTopicDef } from '../../app/registry/types';
import type { LineId } from '../../core/model/types';
import type { MessageKey } from '../../i18n/types';
import type { Unsubscribe } from '../../core/util/types';
import type { DocStore } from '../../state/types';
import { clampPreview, keyLinesAt, propsOfGroup, type PlayheadGroup } from './commands';
import {
  isAssetField,
  isPublishField,
  isSettingsField,
  requestFocusField,
  setPendingSettingsField,
  settingsSectionOf,
} from './focusField';
import { InspectorPanel } from './InspectorPanel';
import { m } from './inspector.messages';
import { linesInExportOrder, musicDuration, previewRangeIssue } from './model';
import { ProjectSettingsDialog } from './ProjectSettingsDialog';
import { keySectionDef } from './sections/KeySection';
import { lineSectionDef } from './sections/LineSection';
import { markerSectionDef } from './sections/MarkerSection';
import { multiLineSectionDef } from './sections/MultiLineSection';
import { multiNoteSectionDef } from './sections/MultiNoteSection';
import { noteSectionDef } from './sections/NoteSection';
import { segmentSectionDef } from './sections/SegmentSection';
import { tempoSectionDef } from './sections/TempoSection';
import { selectedTextSegments, textSegmentSectionDef } from './sections/TextSegmentSection';
import { TextModeDialog } from './TextModeDialog';
import { playheadBeat, runTx, tempoOf } from './tx';
import { FIELD } from './useFieldProblems';

// ---------------------------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------------------------

const SECTIONS = ['display', 'gameplay', 'speed', 'export'] as const;
type SettingsSection = (typeof SECTIONS)[number];
const GROUPS: readonly PlayheadGroup[] = ['alpha', 'position', 'rotate', 'speed'];

function objArg(args: unknown): Readonly<Record<string, unknown>> {
  return typeof args === 'object' && args !== null ? (args as Readonly<Record<string, unknown>>) : {};
}

function sectionArg(args: unknown): SettingsSection | undefined {
  const v = typeof args === 'string' ? args : objArg(args).section;
  return typeof v === 'string' && (SECTIONS as readonly string[]).includes(v) ? (v as SettingsSection) : undefined;
}

function groupArg(args: unknown): PlayheadGroup {
  const v = typeof args === 'string' ? args : objArg(args).group;
  return typeof v === 'string' && (GROUPS as readonly string[]).includes(v) ? (v as PlayheadGroup) : 'alpha';
}

/** Lines the "Set at playhead" command acts on: the selected lines, else the active line. */
function targetLines(ctx: AppServices, store: DocStore): LineId[] {
  const st = ctx.editor.getState();
  const sel = [...st.selection.lines];
  const ids = sel.length > 0 ? sel : st.activeLine === null ? [] : [st.activeLine];
  return linesInExportOrder(
    store.idx,
    ids.filter((id) => store.doc.lines.has(id)),
  );
}

const needStore = (ctx: CommandContext): true | { readonly key: string } =>
  ctx.store === null ? { key: m.needProject } : true;

/** Focuses a field, retrying for a few frames while the inspector mounts the section for a new selection. */
function focusFieldSoon(ctx: AppServices, field: string, tries = 12): void {
  if (requestFocusField(field)) return;
  if (tries <= 0) {
    ctx.ui.toast({ key: m.fieldNotShown }, { kind: 'info' });
    return;
  }
  setTimeout(() => {
    focusFieldSoon(ctx, field, tries - 1);
  }, 16);
}

/**
 * Chart-level fields live in Project settings: an open dialog focuses the field at once; otherwise the dialog opens
 * on the field's section and focuses its control (the pending field), revealing Full-only fields when needed.
 */
function focusSettingsField(ctx: AppServices, field: string): void {
  if (requestFocusField(field)) return;
  const section = settingsSectionOf(field);
  setPendingSettingsField(field);
  void ctx.ui.openDialog(DIALOG.projectSettings, section === null ? {} : { section });
}

function fieldArg(args: unknown): string | null {
  const v = typeof args === 'string' ? args : objArg(args).field;
  return typeof v === 'string' && v !== '' ? v : null;
}

/** Palette choices of `inspector.focusField` (the fields people jump to most). */
const FOCUS_FIELD_CHOICES: ReadonlyArray<{ readonly field: string; readonly label: MessageKey }> = [
  { field: FIELD.noteTime, label: m.fieldNoteTime },
  { field: FIELD.noteX, label: m.fieldNoteX },
  { field: FIELD.noteSpeed, label: m.fieldNoteSpeed },
  { field: FIELD.keyBeat, label: m.fieldKeyBeat },
  { field: FIELD.keyValue, label: m.fieldKeyValue },
  { field: FIELD.segmentEase, label: m.fieldSegmentCurve },
  { field: FIELD.lineName, label: m.fieldLineName },
  { field: FIELD.lineParent, label: m.fieldLineParent },
  { field: FIELD.lineZOrder, label: m.fieldLineZOrder },
  { field: FIELD.pkgSpeedMode, label: m.fieldSpeedMode },
  { field: FIELD.metaPreview, label: m.fieldPreview },
];

// ---------------------------------------------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------------------------------------------

const projectSettings: CommandDef<CommandArgs['project.settings']> = {
  id: 'project.settings',
  title: m.cmdProjectSettings,
  category: 'File',
  icon: 'Settings2',
  aliases: ['project settings', 'chart settings', 'info.yml', 'aspect ratio', 'speed mode', 'song preview', '工程设置'],
  level: 'guided',
  enabled: needStore,
  args: {
    kind: 'choice',
    label: { key: m.argSection },
    options: [
      { value: { section: 'display' }, label: { key: m.psDisplay } },
      { value: { section: 'gameplay' }, label: { key: m.psGameplay } },
      { value: { section: 'speed' }, label: { key: m.psSpeed } },
      { value: { section: 'export' }, label: { key: m.psExport } },
    ],
  },
  menus: [
    { menu: 'app.project', group: 'settings', order: 10 },
    { menu: 'inspector.toolbar', group: 'project', order: 10 },
  ],
  run: async (ctx, args) => {
    const section = sectionArg(args);
    await ctx.ui.openDialog(DIALOG.projectSettings, section === undefined ? {} : { section });
  },
};

const focusField: CommandDef<CommandArgs['inspector.focusField']> = {
  id: 'inspector.focusField',
  title: m.cmdFocusField,
  category: 'View',
  icon: 'TextCursorInput',
  level: 'guided',
  args: {
    kind: 'choice',
    label: { key: m.argField },
    options: FOCUS_FIELD_CHOICES.map((c) => ({ value: { field: c.field }, label: { key: c.label } })),
  },
  run: async (ctx, args) => {
    const field = fieldArg(args);
    if (field === null) return;
    if (isSettingsField(field)) {
      focusSettingsField(ctx, field);
      return;
    }
    if (isAssetField(field)) {
      ctx.ui.showPanel('assets');
      return;
    }
    if (isPublishField(field)) {
      // Package metadata lives in the Publish workspace's PACKAGE panel (DESIGN.md §4.16), which owns its focus.
      await ctx.registry.run('publish.focusField', { field }, 'api');
      return;
    }
    ctx.ui.focusRegion('chart.inspector');
    focusFieldSoon(ctx, field);
  },
};

const focusInspector: CommandDef = {
  id: 'inspector.focus',
  title: m.cmdFocus,
  category: 'View',
  icon: 'PanelRight',
  level: 'guided',
  run: (ctx) => {
    ctx.ui.focusRegion('chart.inspector');
  },
};

const toggleUnits: CommandDef = {
  id: 'inspector.toggleUnits',
  title: m.cmdToggleUnits,
  category: 'View',
  icon: 'Ruler',
  aliases: ['units', 'raw units', 'rpe units', 'percent'],
  level: 'full',
  checked: (ctx) => ctx.settings.value.editing.units === 'rpe',
  run: (ctx) => {
    ctx.settings.set('editing', 'units', ctx.settings.value.editing.units === 'rpe' ? 'friendly' : 'rpe');
  },
};

const setAtPlayhead: CommandDef<{ readonly group?: PlayheadGroup } | undefined> = {
  id: 'inspector.setAtPlayhead',
  title: m.cmdSetAtPlayhead,
  category: 'Motion',
  icon: 'KeyRound',
  aliases: ['keyframe lines', 'key all lines', 'set at playhead'],
  level: 'full',
  args: {
    kind: 'choice',
    label: { key: m.argProperty },
    options: [
      { value: { group: 'alpha' }, label: { key: m.propAlpha } },
      { value: { group: 'position' }, label: { key: m.setPosition } },
      { value: { group: 'rotate' }, label: { key: m.propRotate } },
      { value: { group: 'speed' }, label: { key: m.propSpeed } },
    ],
  },
  enabled: (ctx) => {
    if (ctx.store === null) return { key: m.needProject };
    return targetLines(ctx, ctx.store).length > 0 ? true : { key: m.needLines };
  },
  run: (ctx, args) => {
    const store = ctx.store;
    if (store === null) return;
    const lines = targetLines(ctx, store);
    if (lines.length === 0) return;
    const group = groupArg(args);
    const st = ctx.editor.getState();
    const layer = ctx.settings.value.general.level === 'full' ? st.activeLayer : 0;
    const t = playheadBeat(ctx, tempoOf(store));
    runTx(
      ctx,
      store,
      { label: { key: m.txSetAtPlayhead, params: { count: lines.length } }, command: 'inspector.setAtPlayhead' },
      (tx) => keyLinesAt(tx, { lines, props: propsOfGroup(group), layer, t }),
    );
  },
};

function storeDuration(ctx: AppServices, store: DocStore): number | null {
  return musicDuration(store.doc, ctx.audio.music);
}

const clampPreviewRange: CommandDef = {
  id: 'inspector.clampPreviewRange',
  title: m.cmdClampPreview,
  category: 'File',
  icon: 'Scissors',
  aliases: ['preview range', 'song select preview', 'PH-36'],
  level: 'guided',
  enabled: (ctx) => {
    const store = ctx.store;
    if (store === null) return { key: m.needProject };
    const d = storeDuration(ctx, store);
    if (d === null) return { key: m.previewNoMusic };
    const meta = store.doc.meta;
    return previewRangeIssue(meta.previewStart, meta.previewEnd, d)?.kind === 'beyondSong'
      ? true
      : { key: m.previewAlreadyFits };
  },
  run: (ctx) => {
    const store = ctx.store;
    if (store === null) return;
    const d = storeDuration(ctx, store);
    if (d === null) return;
    const out = runTx(ctx, store, { label: { key: m.txClampPreview }, command: 'inspector.clampPreviewRange' }, (tx) =>
      clampPreview(tx, { durationSec: d }),
    );
    if (out.ok && out.value) {
      const meta = store.doc.meta;
      ctx.ui.toast(
        { key: m.previewClamped, params: { start: meta.previewStart, end: meta.previewEnd ?? meta.previewStart + 15 } },
        { kind: 'success', undo: true },
      );
    }
  },
};

const textMode: CommandDef = {
  id: 'inspector.textMode',
  title: m.cmdTextMode,
  category: 'Motion',
  icon: 'Type',
  aliases: ['text mode', 'type on', 'counter', 'erase'],
  level: 'guided',
  enabled: (ctx) =>
    selectedTextSegments(ctx.editor.getState().selection, ctx.store).length > 0 ? true : { key: m.needTextSegment },
  run: async (ctx) => {
    const first = selectedTextSegments(ctx.editor.getState().selection, ctx.store)[0];
    if (first === undefined) return;
    await ctx.ui.openDialog(DIALOG.textMode, { segment: first });
  },
};

export const INSPECTOR_COMMANDS: readonly CommandDef[] = [
  projectSettings,
  focusField,
  focusInspector,
  toggleUnits,
  setAtPlayhead,
  clampPreviewRange,
  textMode,
];

// ---------------------------------------------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------------------------------------------

const HELP: readonly HelpTopicDef[] = [
  {
    id: 'inspector.expressions',
    title: m.helpExpressionsTitle,
    body: m.helpExpressionsBody,
    showMe: { panel: 'inspector' },
    level: 'guided',
  },
  {
    id: 'inspector.projectSettings',
    title: m.helpProjectSettingsTitle,
    body: m.helpProjectSettingsBody,
    level: 'guided',
  },
  { id: 'inspector.units', title: m.helpUnitsTitle, body: m.helpUnitsBody, level: 'full' },
];

// ---------------------------------------------------------------------------------------------------------------
// Music replacement watcher
// ---------------------------------------------------------------------------------------------------------------

/**
 * Watches the open project's music: when it changes (a replaced file) and the song-select preview no longer fits
 * the new song, a warning toast offers the clamp. Follows project switches.
 */
export function watchPreviewRange(services: AppServices): () => void {
  let store: DocStore | null = null;
  let off: Unsubscribe | null = null;
  let music: string | null = null;
  const check = (): void => {
    if (store === null) return;
    const next = store.doc.meta.music;
    if (next === music) return;
    music = next;
    const d = musicDuration(store.doc, services.audio.music);
    if (d === null) return;
    const meta = store.doc.meta;
    const issue = previewRangeIssue(meta.previewStart, meta.previewEnd, d);
    if (issue?.kind !== 'beyondSong') return;
    services.ui.toast(
      { key: m.previewBeyondAfterMusic, params: { limit: Math.round(issue.limit * 1000) / 1000 } },
      {
        kind: 'warning',
        action: { label: { key: m.clampAction }, command: 'inspector.clampPreviewRange' },
        durationMs: 12000,
      },
    );
  };
  const attach = (): void => {
    if (services.store === store) return;
    off?.();
    off = null;
    store = services.store;
    music = store?.doc.meta.music ?? null;
    if (store === null) return;
    off = store.subscribeVersions(['meta'], check);
  };
  attach();
  const offChange = services.onChange(attach);
  return () => {
    offChange();
    off?.();
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Module
// ---------------------------------------------------------------------------------------------------------------

export default {
  id: 'inspector',
  commands: INSPECTOR_COMMANDS,
  panels: [
    {
      id: 'inspector',
      title: m.panelTitle,
      icon: 'PanelRight',
      slot: 'chart.inspector',
      order: 0,
      level: 'guided',
      component: InspectorPanel,
    },
  ],
  dialogs: [
    { id: DIALOG.textMode, kind: 'modal', component: TextModeDialog },
    { id: DIALOG.projectSettings, kind: 'modal', component: ProjectSettingsDialog },
  ],
  inspectorSections: [
    noteSectionDef,
    multiNoteSectionDef,
    textSegmentSectionDef,
    segmentSectionDef,
    keySectionDef,
    lineSectionDef,
    multiLineSectionDef,
    tempoSectionDef,
    markerSectionDef,
  ],
  helpTopics: HELP,
  init: (services) => watchPreviewRange(services),
} satisfies FeatureModule;
